import { ChatMessage } from "../ChatBox/types";
import { ChatComponentFiles } from "./stores";
import { type UploadFileStatus } from "../Upload/Upload";
import { SetStateAction, useEffect, RefObject, useState } from "react";
import { MessageStream, MessageStreamEvent } from "../../graphql-types/graphql";

export function formatMessage(stream: MessageStream): ChatMessage {
  return {
    type: "agent",
    id: stream.id,
    data: {
      type: "markdown",
      message: stream.message ?? "",
    },
  };
}

export const useContainerWidth = (ref: RefObject<HTMLElement>) => {
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const handleResize = () => {
      if (ref.current) {
        setWidth(ref.current.offsetWidth);
      }
    };

    // Set initial width
    handleResize();

    // Use ResizeObserver for better container resize detection
    let resizeObserver: ResizeObserver | undefined;
    if (ref.current && window.ResizeObserver) {
      resizeObserver = new ResizeObserver(handleResize);
      resizeObserver.observe(ref.current);
    } else {
      // Fallback to window resize
      window.addEventListener("resize", handleResize);
    }

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      } else {
        window.removeEventListener("resize", handleResize);
      }
    };
  }, [ref]);

  return width;
};

export function handleMessageStream({
  messageStream,
  setFetchingResponse,
  updatePrintedMessages,
  handleStopStream,
  streamingId,
  setStreamingId,
}: {
  messageStream: MessageStream;
  setFetchingResponse: (fetchingResponse: boolean) => void;
  updatePrintedMessages: (value: SetStateAction<ChatMessage[]>) => void;
  handleStopStream?: () => void;
  streamingId?: string;
  setStreamingId?: React.Dispatch<React.SetStateAction<string | undefined>>;
}) {
  if (messageStream.event === MessageStreamEvent.Start) {
    setStreamingId?.(messageStream.id);
    // rendering the agent chat header
    setFetchingResponse(false);
  }
  if (messageStream.event === MessageStreamEvent.Stop) {
    setStreamingId?.(void 0);
    handleStopStream?.();
  }
  if (messageStream.event === MessageStreamEvent.Message) {
    // used for mid-stream cancellations, only check if setStreamingId is passed in
    if (setStreamingId && streamingId !== messageStream.id) return;

    updatePrintedMessages(messages => {
      let existing = false;
      const newMessages = messages.map(message => {
        if (messageStream.id !== message.id) {
          return message;
        }
        existing = true;
        if (message.data.type !== "markdown") {
          throw Error(
            "Expecting existing message block to be of 'markdown' message type"
          );
        }
        return {
          ...message,
          data: {
            ...message.data,
            message: message.data.message + messageStream.message,
          },
        };
      });
      if (!existing) {
        newMessages.push(formatMessage(messageStream));
      }
      return newMessages;
    });
  }
}

/**
 * Merge the Upload component's `onChange` list into the file list we hold in the
 * store, without losing the S3 coordinates of files we have already uploaded.
 *
 * Two things make a naive `setFileList(info.fileList)` lose attachments:
 *  1. The Upload wrapper reports a freshly SELECTED file as `status: "done"`,
 *     before our presigned upload has run — so the chip goes green and the send
 *     path (which only sends "done" files) can ship a file with no bucket/key,
 *     which the server then drops. Until it has coordinates, it is "uploading".
 *  2. Its entries carry no `bucket`/`key`/`url` — those are ours, set once the
 *     upload finishes — so replacing the list wholesale wipes them.
 *
 * The Upload component stays authoritative for MEMBERSHIP (adds and removals);
 * an entry we have already uploaded wins over its coordinate-less copy.
 */
export function mergeUploadedFileList(
  prev: ChatComponentFiles[],
  incoming: ChatComponentFiles[]
): ChatComponentFiles[] {
  return incoming.map(file => {
    const uploaded = prev.find(p => p.uid === file.uid);
    if (uploaded?.bucket && uploaded?.key) return uploaded;
    return { ...file, status: "uploading" as UploadFileStatus };
  });
}

/**
 * A slash only starts a skill command at the very start of the message or
 * directly after whitespace, and the command name runs to the caret. That keeps
 * the menu off text where a slash means something else — "http://x", "9/24",
 * "and/or" — while still opening it mid-message.
 *
 * `$1` is the start-or-whitespace prefix, `$2` the (possibly empty) partial name.
 * The name charset mirrors VALID_SKILL_NAME (entity-models ChatSkill.types), the
 * source of truth for what a skill may be called.
 */
/**
 * A command is a sigil plus a name, at the start of the message or directly
 * after whitespace, running to the caret.
 *
 * The anchor is what keeps the menu off text where the sigil means something
 * else — "http://x", "9/24", "and/or" for a slash, and for "@" the case that
 * matters most: an email address, where the "@" never follows whitespace.
 *
 * `$1` is the start-or-whitespace prefix, `$2` the (possibly empty) partial
 * name.
 */
const COMMAND_AT_CARET: Record<CommandSigil, RegExp> = {
  "/": /(^|\s)\/([A-Za-z0-9_-]*)$/,
  "@": /(^|\s)@([A-Za-z0-9_-]*)$/,
};

export type CommandSigil = "/" | "@";

/**
 * The partial name being typed at `caret`, or null when the caret is not
 * inside a command. An empty string means the user has typed just the sigil.
 */
export function commandQuery(
  text: string,
  caret: number,
  sigil: CommandSigil
): string | null {
  // A caret out of step with the text is not reachable from the composer
  // today (both update on the same change) but nothing here enforces that,
  // and a stale caret would silently read the wrong span rather than do
  // nothing. Out of range means "no command", the same as no match.
  if (caret < 0 || caret > text.length) return null;
  const match = text.slice(0, caret).match(COMMAND_AT_CARET[sigil]);
  return match ? match[2] : null;
}

/**
 * Replace only the command at `caret` with `<sigil><name> `, leaving the rest
 * of the message intact, and report where the caret belongs afterwards.
 */
export function replaceCommand(
  text: string,
  caret: number,
  sigil: CommandSigil,
  name: string
): { text: string; caret: number } {
  // Same guard as commandQuery: splicing at an offset that does not match the
  // text would corrupt the message rather than leave it alone.
  if (caret < 0 || caret > text.length) return { text, caret };
  const match = text.slice(0, caret).match(COMMAND_AT_CARET[sigil]);
  if (!match) return { text, caret };
  // The sigil sits exactly one char before the partial name that ends at the caret.
  const start = caret - match[2].length - 1;
  const rest = text.slice(caret);
  // The command is always followed by a space, but mid-message there usually is
  // one already — reuse it rather than doubling it, and leave the caret past it.
  // Only a literal space counts: a newline ends the line the command is on, so
  // it is not the command's separator and the caret must not jump over it.
  const hasSpace = rest.startsWith(" ");
  const inserted = hasSpace ? `${sigil}${name}` : `${sigil}${name} `;
  return {
    text: text.slice(0, start) + inserted + rest,
    caret: start + inserted.length + (hasSpace ? 1 : 0),
  };
}

/** Skills, by slash. Kept as a name so the call sites read as what they do. */
export function slashCommandQuery(text: string, caret: number): string | null {
  return commandQuery(text, caret, "/");
}

export function replaceSlashCommand(
  text: string,
  caret: number,
  skillName: string
): { text: string; caret: number } {
  return replaceCommand(text, caret, "/", skillName);
}

/**
 * Whether a message opens a new speaker block (and so gets a head): the user,
 * the chat's own assistant, and each persistent agent are separate speakers,
 * so two agents answering back to back each get their own name.
 */
export function startsNewSpeakerRun(
  previous: { type: string; speaker?: { key: string } } | undefined,
  message: { type: string; speaker?: { key: string } }
): boolean {
  if (!previous) return true;
  return previous.type !== message.type || previous.speaker?.key !== message.speaker?.key;
}
